import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, BookOpen, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import bisSchemeOneData from '../data/bisSchemeOneProducts.json';
import beeProductData from '../data/beeProducts.json';

const virtualIsiPages = bisSchemeOneData.products.map((item) => ({
  route: item.route,
  title: `${item.product} ISI Certification | ${item.standard}`,
  description: `BIS Scheme-I product route for ${item.product} under ${item.standard}.`,
  category: 'Product Certifications',
}));

const virtualBeePages = [...new Map(beeProductData.products.map((item) => [item.route, {
  route: item.route,
  title: `${item.product} BEE Registration & Star Label`,
  description: `${item.scheme} BEE appliance route with official schedule and sample-label links.`,
  category: 'Product Certifications',
}])).values()];

const additionalServicePages = [{
  route: '/gem-registration',
  title: 'GeM Registration & Account Management Services',
  description: 'End-to-end GeM registration, vendor assessment, catalogue, tender, order and account management support.',
  category: 'Services',
}, {
  route: '/legal-metrology-compliance-services',
  title: 'Legal Metrology Compliance Services',
  description: 'End-to-end packaged commodity registration, declarations, label audit, Rule 27 and ongoing compliance support.',
  category: 'Services',
}];

const PageDirectory = () => {
  const [pages, setPages] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.title = 'All Services & Resources | Complise India';
    fetch('/legacy-pages/manifest.json')
      .then((response) => response.json())
      .then((manifest) => setPages([...new Map(
        [...virtualIsiPages, ...virtualBeePages, ...additionalServicePages, ...manifest].map((page) => [page.route, page]),
      ).values()]))
      .catch(() => setPages([...virtualIsiPages, ...virtualBeePages, ...additionalServicePages]));
  }, []);

  const visiblePages = useMemo(() => {
    const term = query.trim().toLowerCase();
    return pages.filter((page) => {
      const matchesCategory = category === 'All' || page.category === category;
      const matchesTerm = !term || `${page.title} ${page.description} ${page.route}`.toLowerCase().includes(term);
      return matchesCategory && matchesTerm;
    });
  }, [pages, query, category]);

  const categories = useMemo(() => {
    const counts = pages.reduce((result, page) => {
      result[page.category] = (result[page.category] || 0) + 1;
      return result;
    }, {});
    return [['All', pages.length], ...Object.entries(counts).sort(([a], [b]) => a.localeCompare(b))];
  }, [pages]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-neutral-900">
      <Header />
      <main>
        <section className="relative overflow-hidden bg-white border-b border-neutral-200">
          <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-rose-100/70 blur-3xl" />
          <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 py-16 md:py-24">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[#C41E3A] mb-5">
              <BookOpen className="w-4 h-4" /> Complete knowledge library
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold leading-[1.02] max-w-4xl mb-5">
              Every compliance service and resource, now in one place.
            </h1>
            <p className="text-base md:text-lg text-neutral-600 leading-relaxed max-w-2xl mb-8">
              Search the complete Complise India library of certification routes, product guidance, industry pages, insights and global market-access resources.
            </p>
            <label className="max-w-2xl h-14 flex items-center gap-3 rounded-2xl bg-[#FDFBF7] border border-neutral-200 px-5 focus-within:border-[#C41E3A] focus-within:ring-4 focus-within:ring-rose-100 transition-all">
              <Search className="w-5 h-5 text-[#C41E3A] shrink-0" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search BIS, WPC, medical devices, global approvals…"
                className="w-full bg-transparent outline-none text-sm md:text-base placeholder:text-neutral-400"
              />
            </label>
            <div className="flex flex-wrap gap-2 mt-5">
              {categories.map(([name, count]) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setCategory(name)}
                  className={`h-9 px-4 rounded-full text-xs font-bold border transition-colors ${
                    category === name
                      ? 'bg-[#C41E3A] border-[#C41E3A] text-white'
                      : 'bg-white border-neutral-200 text-neutral-700 hover:border-rose-300 hover:text-[#C41E3A]'
                  }`}
                >
                  {name} <span className="opacity-70">{count}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14 md:py-20">
          <div className="flex items-center justify-between gap-4 mb-8">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold">Available pages</h2>
            <span className="text-sm font-semibold text-neutral-500">{visiblePages.length} of {pages.length}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visiblePages.map((page) => (
              <a
                key={page.route}
                href={page.route}
                className="group min-h-44 rounded-3xl bg-white border border-neutral-200 p-6 hover:-translate-y-1 hover:border-rose-200 hover:shadow-[0_20px_50px_-32px_rgba(196,30,58,0.6)] transition-all"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#C41E3A]">
                    {page.category}
                  </span>
                  <span className="w-9 h-9 rounded-full bg-rose-50 group-hover:bg-[#C41E3A] flex items-center justify-center transition-colors">
                    <ArrowUpRight className="w-4 h-4 text-[#C41E3A] group-hover:text-white transition-colors" />
                  </span>
                </div>
                <h3 className="font-display text-lg font-extrabold leading-snug text-neutral-900 mb-2 group-hover:text-[#C41E3A] transition-colors">
                  {page.title}
                </h3>
                <p className="text-xs text-neutral-500 line-clamp-2 leading-relaxed">{page.description}</p>
              </a>
            ))}
          </div>
          {pages.length > 0 && visiblePages.length === 0 && (
            <div className="text-center rounded-3xl border border-dashed border-neutral-300 bg-white px-6 py-16">
              <Search className="w-8 h-8 text-neutral-400 mx-auto mb-3" />
              <p className="font-semibold text-neutral-800">No page matches “{query}”.</p>
              <button onClick={() => setQuery('')} className="mt-3 text-sm font-bold text-[#C41E3A]">Clear search</button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PageDirectory;
